/**
 * Apartsales — полный 9-шаговый чекаут (веха v3-4, канон 29).
 *
 * Маршрут /checkout — транзакционный визард покупки юнита онлайн.
 * Шаги 3-9: KYC → Lock 🔒 → Договор → Эскроу → [Ипотека] → Оплата →
 * Регистрация. State-машина — useCheckout (localStorage).
 *
 * Шаги, требующие внешних систем (ЕСИА, Госключ, скоринг банков,
 * эскроу-API, Росреестр), завершаются симуляцией внутри визарда —
 * боевое подключение этих API вынесено в отдельную инфраструктурную
 * задачу. Сам визард, прогресс, состояние и поток данных — реальные.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';

import { fetchUnitOverview, type UnitOverview } from '~/lib/overview-api';
import { fetchProjectParking, type ParkingItem } from '~/lib/parking-api';
import { getUnitAddonIds } from '~/lib/useUnitAddons';
import {
  CHECKOUT_STEPS,
  useCheckout,
  type CheckoutState,
  type CheckoutStep,
  type Tariff,
} from '~/lib/useCheckout';

export const Route = createFileRoute('/checkout')({
  component: CheckoutWizard,
  validateSearch: (search) => ({
    unit_id: typeof search.unit_id === 'string' ? search.unit_id : undefined,
    mortgage: typeof search.mortgage === 'string' ? search.mortgage : undefined,
  }),
});

/* ── Хелперы ──────────────────────────────────────────────────────────── */

function money(v: number): string {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(2).replace('.', ',')} млн ₽`;
  if (v >= 1000) return `${Math.round(v / 1000)} тыс ₽`;
  return `${Math.round(v)} ₽`;
}

/** Аннуитетный платёж. */
function annuity(principal: number, annualRatePct: number, years: number): number {
  const r = annualRatePct / 100 / 12;
  const n = years * 12;
  if (r <= 0) return principal / n;
  return (principal * r * (1 + r) ** n) / ((1 + r) ** n - 1);
}

const BRAND = '#2A96FB';

interface Addon extends ParkingItem {
  kind: 'parking' | 'storage';
}

/* ── Корневой компонент ───────────────────────────────────────────────── */

function CheckoutWizard() {
  const navigate = useNavigate();
  const { unit_id: unitId, mortgage: mortgageParam } = Route.useSearch();
  const { state, patch, completeAndAdvance, goTo, reset } = useCheckout(unitId ?? '');

  const [ov, setOv] = useState<UnitOverview | null>(null);
  const [addons, setAddons] = useState<Addon[]>([]);
  const [loading, setLoading] = useState(true);

  // загрузка юнита + выбранных допов
  useEffect(() => {
    if (!unitId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([fetchUnitOverview(unitId), fetchProjectParking(unitId)]).then(
      ([overview, parking]) => {
        if (cancelled) return;
        setOv(overview);
        if (parking) {
          const selected = new Set(getUnitAddonIds(unitId));
          const all: Addon[] = [
            ...parking.parking.map((p) => ({ ...p, kind: 'parking' as const })),
            ...parking.storage.map((p) => ({ ...p, kind: 'storage' as const })),
          ];
          setAddons(all.filter((a) => selected.has(a.id)));
        }
        setLoading(false);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [unitId]);

  // приём параметра ?mortgage= из карточки (предвыбор банка + флаг ипотеки)
  useEffect(() => {
    if (mortgageParam && unitId && !state.withMortgage) {
      patch({ withMortgage: true, mortgageBank: mortgageParam });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mortgageParam, unitId]);

  const unitPrice = ov?.money?.price ?? 0;
  const addonsTotal = useMemo(() => addons.reduce((s, a) => s + a.price, 0), [addons]);
  const dealTotal = unitPrice + addonsTotal;

  if (!unitId) {
    return (
      <Shell>
        <Centered text="Чекаут открывается из карточки юнита." />
      </Shell>
    );
  }
  if (loading) {
    return (
      <Shell>
        <Centered text="Готовим сделку…" />
      </Shell>
    );
  }

  const unitName = ov?.complex?.name
    ? `ЖК «${ov.complex.name}»`
    : 'Юнит';

  const ctx: StepCtx = {
    state,
    patch,
    advance: completeAndAdvance,
    unitName,
    unitPrice,
    addons,
    addonsTotal,
    dealTotal,
    onExit: () => navigate({ to: '/feed' }),
    onRestart: () => {
      reset();
      goTo('kyc');
    },
  };

  return (
    <Shell>
      <TopBar onExit={ctx.onExit} />
      {state.current !== 'done' && (
        <ProgressBar current={state.current} completed={state.completed} withMortgage={state.withMortgage} />
      )}
      <div style={{ marginTop: 22 }}>
        {state.current === 'kyc' && <StepKyc ctx={ctx} />}
        {state.current === 'lock' && <StepLock ctx={ctx} />}
        {state.current === 'contract' && <StepContract ctx={ctx} />}
        {state.current === 'escrow' && <StepEscrow ctx={ctx} />}
        {state.current === 'mortgage' && <StepMortgage ctx={ctx} />}
        {state.current === 'pay' && <StepPay ctx={ctx} />}
        {state.current === 'register' && <StepRegister ctx={ctx} />}
        {state.current === 'done' && <StepDone ctx={ctx} />}
      </div>
    </Shell>
  );
}

interface StepCtx {
  state: CheckoutState;
  patch: (p: Partial<CheckoutState>) => void;
  advance: () => void;
  unitName: string;
  unitPrice: number;
  addons: Addon[];
  addonsTotal: number;
  dealTotal: number;
  onExit: () => void;
  onRestart: () => void;
}

/* ── Каркас и общие элементы ──────────────────────────────────────────── */

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-bg, #F2F2F7)' }}>
      <div style={{ maxWidth: 480, margin: '0 auto', padding: '14px 16px 56px' }}>{children}</div>
    </div>
  );
}

function TopBar({ onExit }: { onExit: () => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 36 }}>
      <button
        type="button"
        onClick={onExit}
        aria-label="Закрыть оформление"
        style={{
          minHeight: 44,
          display: 'flex',
          alignItems: 'center',
          fontSize: 14,
          color: 'var(--color-label-2)',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
        }}
      >
        ✕ Закрыть
      </button>
      <span style={{ fontSize: 13, fontWeight: 700, color: BRAND }}>Apartsales</span>
    </div>
  );
}

function ProgressBar({
  current,
  completed,
  withMortgage,
}: {
  current: CheckoutStep;
  completed: CheckoutStep[];
  withMortgage: boolean;
}) {
  const steps = CHECKOUT_STEPS.filter((s) => s.id !== 'mortgage' || withMortgage);
  const cur = steps.find((s) => s.id === current);
  return (
    <div style={{ marginTop: 14 }}>
      <div
        style={{ display: 'flex', gap: 5 }}
        role="progressbar"
        aria-valuemin={1}
        aria-valuemax={9}
        aria-valuenow={cur?.no ?? 1}
        aria-label={`Шаг ${cur?.no ?? '—'} из 9: ${cur?.label ?? ''}`}
      >
        {steps.map((s) => {
          const done = completed.includes(s.id);
          const active = s.id === current;
          return (
            <div
              key={s.id}
              aria-hidden="true"
              style={{
                flex: 1,
                height: 5,
                borderRadius: 999,
                background: done || active ? BRAND : 'rgba(0,0,0,0.10)',
                opacity: done || active ? 1 : 1,
              }}
            />
          );
        })}
      </div>
      <div style={{ marginTop: 8, fontSize: 13, fontWeight: 600, color: 'var(--color-label-2)' }}>
        Шаг {cur?.no ?? '—'} из 9 · {cur?.label ?? ''}
      </div>
    </div>
  );
}

function StepFrame({
  eyebrow,
  title,
  subtitle,
  children,
}: {
  eyebrow: string;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.6, color: BRAND }}>{eyebrow}</div>
        <h1 style={{ margin: '4px 0 0', fontSize: 22, fontWeight: 800, color: 'var(--color-label)', letterSpacing: -0.4 }}>
          {title}
        </h1>
        {subtitle && (
          <p style={{ margin: '6px 0 0', fontSize: 13.5, lineHeight: 1.5, color: 'var(--color-label-2)' }}>
            {subtitle}
          </p>
        )}
      </div>
      {children}
    </div>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: 16,
        borderRadius: 18,
        background: '#fff',
        border: '0.5px solid var(--color-glass-stroke, rgba(0,0,0,0.10))',
      }}
    >
      {children}
    </div>
  );
}

function PrimaryButton({ label, onClick, disabled }: { label: string; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        width: '100%',
        height: 52,
        borderRadius: 999,
        border: 'none',
        fontSize: 15,
        fontWeight: 700,
        color: '#fff',
        background: disabled ? 'rgba(0,0,0,0.18)' : BRAND,
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      {label}
    </button>
  );
}

/** Симуляция работы внешней системы: спиннер → завершение. */
function useSimulation(): { running: boolean; done: boolean; run: () => void } {
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  const run = () => {
    setRunning(true);
    window.setTimeout(() => {
      setRunning(false);
      setDone(true);
    }, 1400);
  };
  return { running, done, run };
}

function DealSummary({ ctx }: { ctx: StepCtx }) {
  return (
    <Card>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-label-3)', marginBottom: 8 }}>
        СДЕЛКА
      </div>
      <Row k={ctx.unitName} v={money(ctx.unitPrice)} />
      {ctx.addons.map((a) => (
        <Row key={a.id} k={a.name} v={money(a.price)} muted />
      ))}
      <div style={{ height: 1, background: 'var(--color-glass-stroke, rgba(0,0,0,0.08))', margin: '8px 0' }} />
      <Row k="Итого" v={money(ctx.dealTotal)} bold />
    </Card>
  );
}

function Row({ k, v, bold, muted }: { k: string; v: string; bold?: boolean; muted?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '4px 0' }}>
      <span style={{ fontSize: bold ? 14 : 13, color: muted ? 'var(--color-label-3)' : 'var(--color-label-2)' }}>
        {k}
      </span>
      <span
        style={{
          fontSize: bold ? 15 : 13,
          fontWeight: bold ? 800 : 600,
          color: 'var(--color-label)',
          whiteSpace: 'nowrap',
        }}
      >
        {v}
      </span>
    </div>
  );
}

function Centered({ text }: { text: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{ padding: '80px 20px', textAlign: 'center', fontSize: 14, color: 'var(--color-label-3)' }}
    >
      {text}
    </div>
  );
}

function CheckList({ items }: { items: string[] }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
      {items.map((t) => (
        <div key={t} style={{ display: 'flex', gap: 9, alignItems: 'flex-start' }}>
          <span style={{ color: '#0A7D2E', fontSize: 14, lineHeight: 1.5, flexShrink: 0 }}>✓</span>
          <span style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--color-label)' }}>{t}</span>
        </div>
      ))}
    </div>
  );
}

/* ── Шаг 3 — KYC ──────────────────────────────────────────────────────── */

function StepKyc({ ctx }: { ctx: StepCtx }) {
  const sim = useSimulation();
  const method = ctx.state.kycMethod;

  return (
    <StepFrame
      eyebrow="ШАГ 3 · ЛИЧНОСТЬ"
      title="Подтверждение личности"
      subtitle="Быстрая проверка KYC по требованию 115-ФЗ. Документы никуда не отправляются вручную — только подтверждённый канал."
    >
      <DealSummary ctx={ctx} />

      {!sim.done && (
        <Card>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-label)', marginBottom: 12 }}>
            Способ подтверждения
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <MethodOption
              label="Госуслуги (ЕСИА)"
              hint="Для граждан РФ — за 1 минуту"
              active={method === 'esia'}
              onClick={() => ctx.patch({ kycMethod: 'esia' })}
            />
            <MethodOption
              label="Загранпаспорт"
              hint="Для иностранных покупателей"
              active={method === 'passport'}
              onClick={() => ctx.patch({ kycMethod: 'passport' })}
            />
          </div>
        </Card>
      )}

      {sim.done && (
        <Card>
          <div style={{ fontSize: 15, fontWeight: 800, color: '#0A7D2E', marginBottom: 8 }}>
            ✓ Личность подтверждена
          </div>
          <CheckList items={['Документ проверен', 'AML-скрининг пройден', 'Можно переходить к Lock']} />
        </Card>
      )}

      {!sim.done ? (
        <PrimaryButton
          label={sim.running ? 'Проверяем…' : 'Подтвердить личность'}
          disabled={!method || sim.running}
          onClick={sim.run}
        />
      ) : (
        <PrimaryButton label="Перейти к Lock 🔒" onClick={ctx.advance} />
      )}
    </StepFrame>
  );
}

function MethodOption({
  label,
  hint,
  active,
  onClick,
}: {
  label: string;
  hint: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        textAlign: 'left',
        padding: '12px 14px',
        borderRadius: 13,
        cursor: 'pointer',
        background: active ? 'rgba(42,150,251,0.07)' : 'rgba(0,0,0,0.03)',
        border: `1px solid ${active ? BRAND : 'transparent'}`,
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--color-label)' }}>{label}</div>
      <div style={{ fontSize: 12, color: 'var(--color-label-3)', marginTop: 2 }}>{hint}</div>
    </button>
  );
}

/* ── Шаг 4 — Lock 🔒 ──────────────────────────────────────────────────── */

const TARIFFS: Array<{ id: Tariff; name: string; pct: number; note: string; tone: string }> = [
  { id: 'flex', name: 'Гибкий', pct: 2, note: 'Возврат в 24–48 часов без объяснений', tone: '#0A7D2E' },
  { id: 'standard', name: 'Стандартный', pct: 1, note: '7 дней на следующий шаг · популярный', tone: '#A66800' },
  { id: 'hard', name: 'Жёсткий', pct: 0.5, note: 'Минимальная переплата · 3 дня на шаг', tone: '#C0392B' },
];

function StepLock({ ctx }: { ctx: StepCtx }) {
  const sim = useSimulation();
  const tariff = ctx.state.tariff;
  const chosen = TARIFFS.find((t) => t.id === tariff);
  const deposit = chosen ? Math.round((ctx.dealTotal * chosen.pct) / 100) : 0;

  return (
    <StepFrame
      eyebrow="ШАГ 4 · LOCK 🔒"
      title="Зафиксировать юнит"
      subtitle="Lock снимает юнит с продажи на время сделки. Задаток — возвратный, идёт в счёт оплаты."
    >
      <Card>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-label)', marginBottom: 12 }}>
          Тариф задатка
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {TARIFFS.map((t) => {
            const active = t.id === tariff;
            const sum = Math.round((ctx.dealTotal * t.pct) / 100);
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => ctx.patch({ tariff: t.id })}
                style={{
                  textAlign: 'left',
                  padding: '13px 14px',
                  borderRadius: 13,
                  cursor: 'pointer',
                  background: active ? 'rgba(42,150,251,0.07)' : 'rgba(0,0,0,0.03)',
                  border: `1px solid ${active ? BRAND : 'transparent'}`,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: t.tone }}>{t.name}</span>
                  <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--color-label)' }}>
                    {t.pct}% · {money(sum)}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--color-label-3)', marginTop: 3 }}>{t.note}</div>
              </button>
            );
          })}
        </div>
      </Card>

      <Card>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-label)', marginBottom: 10 }}>
          Способ оплаты остатка
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <PayMethod
            label="Своими средствами"
            active={!ctx.state.withMortgage}
            onClick={() => ctx.patch({ withMortgage: false })}
          />
          <PayMethod
            label="С ипотекой"
            active={ctx.state.withMortgage}
            onClick={() => ctx.patch({ withMortgage: true })}
          />
        </div>
      </Card>

      {sim.done && (
        <Card>
          <div style={{ fontSize: 15, fontWeight: 800, color: '#0A7D2E' }}>✓ Lock активирован</div>
          <div style={{ fontSize: 13, color: 'var(--color-label-2)', marginTop: 6 }}>
            Юнит снят с продажи. Задаток {money(deposit)} зачтётся в оплату.
          </div>
        </Card>
      )}

      {!sim.done ? (
        <PrimaryButton
          label={sim.running ? 'Проводим задаток…' : tariff ? `Внести задаток ${money(deposit)}` : 'Выберите тариф'}
          disabled={!tariff || sim.running}
          onClick={sim.run}
        />
      ) : (
        <PrimaryButton label="Перейти к договору" onClick={ctx.advance} />
      )}
    </StepFrame>
  );
}

function PayMethod({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      style={{
        flex: 1,
        minHeight: 44,
        borderRadius: 11,
        fontSize: 13,
        fontWeight: active ? 700 : 500,
        cursor: 'pointer',
        color: active ? '#fff' : 'var(--color-label-2)',
        background: active ? BRAND : 'rgba(0,0,0,0.04)',
        border: 'none',
      }}
    >
      {label}
    </button>
  );
}

/* ── Шаг 5 — Договор (ДДУ + AI-проверка + Госключ) ───────────────────── */

function StepContract({ ctx }: { ctx: StepCtx }) {
  const gen = useSimulation();
  const sign = useSimulation();

  return (
    <StepFrame
      eyebrow="ШАГ 5 · ДОГОВОР"
      title="Договор долевого участия"
      subtitle="ДДУ формируется автоматически и проверяется ИИ на соответствие 214-ФЗ. Подпись — в приложении Госключ."
    >
      {!gen.done ? (
        <>
          <Card>
            <CheckList
              items={[
                'Шаблон ДДУ под тип юнита',
                'Реквизиты сторон и эскроу-счёта',
                'AI-проверка на риски и соответствие 214-ФЗ',
              ]}
            />
          </Card>
          <PrimaryButton
            label={gen.running ? 'Формируем и проверяем…' : 'Сформировать ДДУ'}
            disabled={gen.running}
            onClick={gen.run}
          />
        </>
      ) : (
        <>
          <Card>
            <div style={{ fontSize: 14, fontWeight: 800, color: '#0A7D2E', marginBottom: 8 }}>
              ✓ ИИ проверил договор — критических рисков не найдено
            </div>
            <CheckList
              items={[
                'Срок передачи юнита указан и соответствует декларации',
                'Эскроу-счёт по 214-ФЗ — расчёт защищён',
                'Условия расторжения соответствуют закону',
                'Скрытых комиссий и штрафов не обнаружено',
              ]}
            />
          </Card>
          {sign.done ? (
            <>
              <Card>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#0A7D2E' }}>✓ Договор подписан</div>
                <div style={{ fontSize: 13, color: 'var(--color-label-2)', marginTop: 6 }}>
                  ДДУ подписан электронной подписью через Госключ.
                </div>
              </Card>
              <PrimaryButton label="Перейти к эскроу" onClick={ctx.advance} />
            </>
          ) : (
            <PrimaryButton
              label={sign.running ? 'Подписываем в Госключе…' : 'Подписать через Госключ'}
              disabled={sign.running}
              onClick={sign.run}
            />
          )}
        </>
      )}
    </StepFrame>
  );
}

/* ── Шаг 6 — Эскроу ──────────────────────────────────────────────────── */

function StepEscrow({ ctx }: { ctx: StepCtx }) {
  const sim = useSimulation();
  const bank = ctx.state.escrowBank ?? 'Сбербанк';

  useEffect(() => {
    if (!ctx.state.escrowBank) ctx.patch({ escrowBank: 'Сбербанк' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StepFrame
      eyebrow="ШАГ 6 · ЭСКРОУ"
      title="Эскроу-счёт"
      subtitle="Деньги хранятся на эскроу-счёте в банке и переходят застройщику только после сдачи дома — защита по 214-ФЗ."
    >
      <Card>
        <Row k="Банк эскроу" v={bank} bold />
        <Row k="Сумма на эскроу" v={money(ctx.dealTotal)} />
        <Row k="Раскрытие счёта" v="После ввода дома в эксплуатацию" />
      </Card>

      {sim.done && (
        <Card>
          <div style={{ fontSize: 15, fontWeight: 800, color: '#0A7D2E' }}>✓ Эскроу-счёт открыт</div>
          <div style={{ fontSize: 13, color: 'var(--color-label-2)', marginTop: 6 }}>
            Счёт в {bank} готов к зачислению средств.
          </div>
        </Card>
      )}

      {!sim.done ? (
        <PrimaryButton
          label={sim.running ? 'Открываем счёт…' : 'Открыть эскроу-счёт'}
          disabled={sim.running}
          onClick={sim.run}
        />
      ) : (
        <PrimaryButton
          label={ctx.state.withMortgage ? 'Перейти к ипотеке' : 'Перейти к оплате'}
          onClick={ctx.advance}
        />
      )}
    </StepFrame>
  );
}

/* ── Шаг 7 — Ипотека (multi-bank scoring) ────────────────────────────── */

interface BankOffer {
  slug: string;
  name: string;
  rate: number;
}
const PARTNER_BANKS: BankOffer[] = [
  { slug: 'sber', name: 'Сбер', rate: 12.4 },
  { slug: 'vtb', name: 'ВТБ', rate: 12.7 },
  { slug: 'tinkoff', name: 'Т-Банк', rate: 11.9 },
  { slug: 'alfa', name: 'Альфа-Банк', rate: 13.1 },
  { slug: 'otkritie', name: 'Открытие', rate: 12.9 },
];

function StepMortgage({ ctx }: { ctx: StepCtx }) {
  const sim = useSimulation();
  const downPct = 20;
  const loan = Math.round((ctx.dealTotal * (100 - downPct)) / 100);
  const years = 25;
  const chosen = ctx.state.mortgageBank;

  return (
    <StepFrame
      eyebrow="ШАГ 7 · ИПОТЕКА"
      title="Заявка в банки"
      subtitle="Apartsales подаёт заявку сразу в несколько банков-партнёров — вы выбираете лучшее одобрение."
    >
      <Card>
        <Row k="Сумма кредита" v={money(loan)} bold />
        <Row k="Первоначальный взнос" v={`${downPct}% · ${money(ctx.dealTotal - loan)}`} />
        <Row k="Срок" v={`${years} лет`} />
      </Card>

      {!sim.done ? (
        <PrimaryButton
          label={sim.running ? 'Отправляем заявки…' : 'Подать заявки в банки'}
          disabled={sim.running}
          onClick={sim.run}
        />
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {PARTNER_BANKS.map((b, i) => {
              // детерминированный исход скоринга по позиции
              const rejected = i === 3;
              const payment = annuity(loan, b.rate, years);
              const isChosen = chosen === b.slug;
              return (
                <button
                  key={b.slug}
                  type="button"
                  disabled={rejected}
                  onClick={() => ctx.patch({ mortgageBank: b.slug })}
                  style={{
                    textAlign: 'left',
                    padding: '13px 14px',
                    borderRadius: 13,
                    cursor: rejected ? 'default' : 'pointer',
                    background: isChosen ? 'rgba(42,150,251,0.07)' : '#fff',
                    border: `1px solid ${isChosen ? BRAND : 'var(--color-glass-stroke, rgba(0,0,0,0.10))'}`,
                    opacity: rejected ? 0.55 : 1,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--color-label)' }}>
                      {rejected ? '🔴' : '🟢'} {b.name}
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 800, color: rejected ? 'var(--color-label-3)' : 'var(--color-label)' }}>
                      {rejected ? 'Отказ' : `${b.rate}%`}
                    </span>
                  </div>
                  {!rejected && (
                    <div style={{ fontSize: 12, color: 'var(--color-label-3)', marginTop: 3 }}>
                      Платёж ≈ {money(payment)}/мес
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          <PrimaryButton
            label={chosen ? 'Зафиксировать банк и продолжить' : 'Выберите банк'}
            disabled={!chosen}
            onClick={ctx.advance}
          />
        </>
      )}
    </StepFrame>
  );
}

/* ── Шаг 8 — Оплата ──────────────────────────────────────────────────── */

function StepPay({ ctx }: { ctx: StepCtx }) {
  const sim = useSimulation();
  const tariff = TARIFFS.find((t) => t.id === ctx.state.tariff);
  const deposit = tariff ? Math.round((ctx.dealTotal * tariff.pct) / 100) : 0;
  const remainder = ctx.dealTotal - deposit;
  const bank = PARTNER_BANKS.find((b) => b.slug === ctx.state.mortgageBank);

  return (
    <StepFrame
      eyebrow="ШАГ 8 · ОПЛАТА"
      title="Зачисление на эскроу"
      subtitle={
        ctx.state.withMortgage
          ? 'Первоначальный взнос вносите вы, остаток переводит банк по одобренной ипотеке.'
          : 'Остаток суммы зачисляется на защищённый эскроу-счёт.'
      }
    >
      <Card>
        <Row k="Полная стоимость" v={money(ctx.dealTotal)} />
        <Row k="Задаток (внесён)" v={`− ${money(deposit)}`} muted />
        <div style={{ height: 1, background: 'var(--color-glass-stroke, rgba(0,0,0,0.08))', margin: '8px 0' }} />
        <Row k="К зачислению" v={money(remainder)} bold />
        {ctx.state.withMortgage && bank && (
          <div style={{ fontSize: 12, color: 'var(--color-label-3)', marginTop: 8 }}>
            Ипотека одобрена в «{bank.name}» — банк переводит кредитную часть на эскроу.
          </div>
        )}
      </Card>

      {sim.done && (
        <Card>
          <div style={{ fontSize: 15, fontWeight: 800, color: '#0A7D2E' }}>✓ Средства на эскроу-счёте</div>
          <div style={{ fontSize: 13, color: 'var(--color-label-2)', marginTop: 6 }}>
            Деньги защищены до сдачи дома. Можно подавать на регистрацию.
          </div>
        </Card>
      )}

      {!sim.done ? (
        <PrimaryButton
          label={sim.running ? 'Проводим оплату…' : 'Оплатить'}
          disabled={sim.running}
          onClick={sim.run}
        />
      ) : (
        <PrimaryButton label="Перейти к регистрации" onClick={ctx.advance} />
      )}
    </StepFrame>
  );
}

/* ── Шаг 9 — Регистрация ─────────────────────────────────────────────── */

function StepRegister({ ctx }: { ctx: StepCtx }) {
  const sim = useSimulation();

  return (
    <StepFrame
      eyebrow="ШАГ 9 · РЕГИСТРАЦИЯ"
      title="Регистрация в Росреестре"
      subtitle="Apartsales подаёт ДДУ на регистрацию в Росреестр в электронном виде — без визита в МФЦ."
    >
      <Card>
        <CheckList
          items={[
            'Электронная подача ДДУ в Росреестр',
            'Отслеживание статуса регистрации',
            'Выписка ЕГРН в Паспорт собственника',
            'Цифровой ключ от юнита',
          ]}
        />
      </Card>

      {sim.done && (
        <Card>
          <div style={{ fontSize: 15, fontWeight: 800, color: '#0A7D2E' }}>✓ ДДУ подан на регистрацию</div>
          <div style={{ fontSize: 13, color: 'var(--color-label-2)', marginTop: 6 }}>
            Статус будет в разделе «Сделки». Выписка ЕГРН придёт в Паспорт собственника.
          </div>
        </Card>
      )}

      {!sim.done ? (
        <PrimaryButton
          label={sim.running ? 'Подаём в Росреестр…' : 'Подать на регистрацию'}
          disabled={sim.running}
          onClick={sim.run}
        />
      ) : (
        <PrimaryButton label="Завершить сделку" onClick={ctx.advance} />
      )}
    </StepFrame>
  );
}

/* ── Финал ───────────────────────────────────────────────────────────── */

function StepDone({ ctx }: { ctx: StepCtx }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, paddingTop: 12 }}>
      <div style={{ textAlign: 'center', paddingTop: 24 }}>
        <div
          style={{
            width: 72,
            height: 72,
            borderRadius: 999,
            margin: '0 auto',
            background: 'rgba(10,125,46,0.10)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 34,
          }}
        >
          ✓
        </div>
        <h1 style={{ margin: '16px 0 0', fontSize: 23, fontWeight: 800, color: 'var(--color-label)' }}>
          Сделка оформлена онлайн
        </h1>
        <p style={{ margin: '8px 0 0', fontSize: 14, lineHeight: 1.5, color: 'var(--color-label-2)' }}>
          Все 9 шагов пройдены без визита в офис. {ctx.unitName} — ваш.
        </p>
      </div>

      <DealSummary ctx={ctx} />

      <Card>
        <CheckList
          items={[
            'ДДУ подписан и подан в Росреестр',
            'Средства защищены на эскроу-счёте',
            'Выписка ЕГРН придёт в Паспорт собственника',
            'Цифровой ключ — после сдачи дома',
          ]}
        />
      </Card>

      <PrimaryButton label="Перейти в «Сделки»" onClick={ctx.onExit} />
      <button
        type="button"
        onClick={ctx.onRestart}
        style={{
          height: 44,
          fontSize: 13,
          color: 'var(--color-label-3)',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
        }}
      >
        Пройти сделку заново
      </button>
    </div>
  );
}
